import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { useMemoizedArrayData, type ApiResponse } from 'src/hooks/api/api';
import {
  CrmSavedViewsService,
  type ApiQueryParams,
  type SavedView,
  type SavedViewCreateBodyParams,
  type SavedViewModelType,
  type SavedViewUpdateBodyParams,
} from 'src/api/generated';

interface UseSavedViewsResponse extends ApiResponse {
  views: SavedView[];
}

interface SavedViewQueryClient {
  invalidateQueries: QueryClient['invalidateQueries'];
}

const noopSavedViewSuccess = (_view: SavedView): void => undefined;

/**
 * Invalidates all saved-view query variants so dropdowns and providers stay in
 * sync after create, update, and delete mutations.
 *
 * @param queryClient - React Query client used by the current view.
 * @returns A promise that resolves once all saved-view query families have
 * been marked stale.
 */
export const invalidateSavedViewQueries = async (
  queryClient: SavedViewQueryClient
): Promise<void> => {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['crm/saved-views'] }),
    queryClient.invalidateQueries({ queryKey: ['crm/saved-views/all'] }),
  ]);
};

/**
 * Hook to fetch saved views with optional pagination support.
 * @param model - The model type to filter views by
 * @param queryParams - Optional pagination and filter parameters
 * @param enabled - Whether the query should be enabled
 */
export const useSavedViews = (
  model: SavedViewModelType,
  queryParams: ApiQueryParams = {},
  enabled = true
): UseSavedViewsResponse => {
  const fetchViews = async (): Promise<SavedView[]> => {
    const { filterBy, searchBy, sortBy, page, pageSize } = queryParams;

    const response = await CrmSavedViewsService.getSavedViews(
      model,
      filterBy,
      searchBy,
      sortBy,
      page,
      pageSize
    );
    return response.items;
  };

  const {
    data,
    error,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: ['crm/saved-views', model, queryParams],
    queryFn: fetchViews,
    enabled,
  });

  const views = useMemoizedArrayData<SavedView>(data ?? []);

  return {
    loading,
    error,
    views,
    refetch,
  };
};

/**
 * Hook to fetch ALL saved views by iterating through all pages.
 * Use this when you truly need all views (e.g., dropdowns with many views).
 * For most cases, use the useSavedViews hook with pagination parameters.
 *
 * @param model - The model type to filter views by
 * @param filters - Optional filters to apply (searchBy, filterBy, sortBy)
 * @returns All views matching the model and filters
 */
export const useAllSavedViews = (
  model: SavedViewModelType,
  filters: {
    filterBy?: string;
    searchBy?: string;
    sortBy?: string;
  } = {}
): UseSavedViewsResponse => {
  const fetchAllViews = async (): Promise<SavedView[]> => {
    const { filterBy, searchBy, sortBy } = filters;
    const PAGE_SIZE = 100;
    const MAX_PAGES = 10;

    let page = 1;
    let allViews: SavedView[] = [];
    let hasMore = true;

    while (hasMore) {
      const response = await CrmSavedViewsService.getSavedViews(
        model,
        filterBy,
        searchBy,
        sortBy,
        page,
        PAGE_SIZE
      );

      allViews = [...allViews, ...response.items];

      hasMore = response.items.length === PAGE_SIZE;
      page++;

      if (page > MAX_PAGES) {
        console.warn(
          `useAllSavedViews: Reached maximum page limit (${MAX_PAGES} pages, ${MAX_PAGES * PAGE_SIZE} views). There may be more views.`
        );
        break;
      }
    }

    return allViews;
  };

  const {
    data,
    error,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: ['crm/saved-views/all', model, filters],
    queryFn: fetchAllViews,
    // Cache for 5 minutes since fetching all views is expensive
    staleTime: 5 * 60 * 1000,
  });

  const views = useMemoizedArrayData<SavedView>(data ?? []);

  return {
    loading,
    error,
    views,
    refetch,
  };
};

interface UseCreateSavedViewMutationResponse {
  createView: (data: SavedViewCreateBodyParams) => Promise<SavedView>;
  loading: boolean;
  error: Error | null;
}

export const useCreateSavedView = (
  onSuccess: (view: SavedView) => void = noopSavedViewSuccess
): UseCreateSavedViewMutationResponse => {
  const queryClient = useQueryClient();

  const createView = async (
    data: SavedViewCreateBodyParams
  ): Promise<SavedView> => {
    return await CrmSavedViewsService.createSavedView(data);
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation({
    mutationFn: createView,
    onSuccess: async (view) => {
      await invalidateSavedViewQueries(queryClient);
      onSuccess(view);
    },
  });

  return {
    createView: mutateAsync,
    loading,
    error,
  };
};

interface UseUpdateSavedViewMutationResponse {
  updateView: (
    params: { id: number } & SavedViewUpdateBodyParams
  ) => Promise<SavedView>;
  loading: boolean;
  error: Error | null;
}

export const useUpdateSavedView = (
  onSuccess: (view: SavedView) => void = noopSavedViewSuccess
): UseUpdateSavedViewMutationResponse => {
  const queryClient = useQueryClient();

  const updateView = async ({
    id,
    ...data
  }: { id: number } & SavedViewUpdateBodyParams): Promise<SavedView> => {
    return await CrmSavedViewsService.updateSavedView(id, data);
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation({
    mutationFn: updateView,
    onSuccess: async (view) => {
      await invalidateSavedViewQueries(queryClient);
      onSuccess(view);
    },
  });

  return {
    updateView: mutateAsync,
    loading,
    error,
  };
};

interface UseDeleteSavedViewMutationResponse {
  deleteView: (id: number) => Promise<SavedView>;
  loading: boolean;
  error: Error | null;
}

export const useDeleteSavedView = (
  onSuccess: (view: SavedView) => void = noopSavedViewSuccess
): UseDeleteSavedViewMutationResponse => {
  const queryClient = useQueryClient();

  const deleteView = async (id: number): Promise<SavedView> => {
    return await CrmSavedViewsService.deleteSavedView(id);
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation({
    mutationFn: deleteView,
    onSuccess: async (view) => {
      await invalidateSavedViewQueries(queryClient);
      onSuccess(view);
    },
  });

  return {
    deleteView: mutateAsync,
    loading,
    error,
  };
};
